import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  CircularProgress,
  Paper,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import { EventData, CalendarDot, CATEGORY_COLORS, CATEGORY_LABELS } from '../../types/event';
import { DeadlineLabel } from './DeadlineLabel';
import { getTodayStr } from '../../utils/dateUtils';

// ─── ユーティリティ ───────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');

const toLocalDateStr = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const addDays = (dateStr: string, days: number): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toLocalDateStr(d);
};

const getSundayOfWeek = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() - d.getDay());
  return toLocalDateStr(d);
};

/** 週ストリップヘッダー用: weekStart の年月を返す */
const formatHeaderMonth = (weekStart: string): string => {
  const d = new Date(`${weekStart}T00:00:00`);
  return `${d.getFullYear()}年${d.getMonth() + 1}月`;
};

/** 週の7日配列（Date[]）を返す */
const getWeekDays = (weekStart: string): Date[] =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(`${weekStart}T00:00:00`);
    d.setDate(d.getDate() + i);
    return d;
  });

/** HH:MM:SS → HH:MM */
const fmtTime = (t: string) => t.slice(0, 5);

const DOW_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

// ─── 型定義 ────────────────────────────────────────────────────────

interface WeekViewCalendarProps {
  weekStart: string;
  weekEnd: string;
  calendarDots: CalendarDot[];
  events: EventData[];
  isLoading: boolean;
  selectedDate: string | null;
  onDateClick: (date: string) => void;
  onWeekChange: (weekStart: string, weekEnd: string) => void;
  onSwitchToMonth: () => void;
  onEventDetail: (event: EventData) => void;
}

// ─── コンパクトリストアイテム ──────────────────────────────────────

interface CompactItemProps {
  event: EventData;
  onDetail: (event: EventData) => void;
}

const CompactItem: React.FC<CompactItemProps> = ({ event, onDetail }) => {
  const isOnline = event.location_type === 'online';
  const isPast = event.is_past;
  const catColor = CATEGORY_COLORS[event.category];

  const baseBg = isPast
    ? '#F5F5F5'
    : event.is_participated
      ? '#f0f7ff'
      : 'transparent';

  const hoverBg = isPast
    ? '#EEEEEE'
    : event.is_participated
      ? '#e3f0ff'
      : '#f8fafc';

  return (
    <Box
      onClick={() => onDetail(event)}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        transition: 'background 0.12s',
        opacity: isPast ? 0.55 : 1,
        bgcolor: baseBg,
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { bgcolor: hoverBg },
      }}
    >

      {/* 時刻列 */}
      <Box
        sx={{
          width: 64,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1.5,
          px: 0.5,
          bgcolor: '#f8fafc',
          borderRight: '1px solid',
          borderColor: 'divider',
          gap: '1px',
        }}
      >
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#2c3e50', lineHeight: 1.2 }}>
          {fmtTime(event.start_time)}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#aab5be', lineHeight: 1 }}>〜</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#6b7c8e', lineHeight: 1.2 }}>
          {fmtTime(event.end_time)}
        </Typography>
      </Box>

      {/* メイン情報 */}
      <Box sx={{ flex: 1, py: 1.25, px: 1.5, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4 }}>

        {/* 企業名 + バッジ行 */}
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.25 }}>
          {/* 企業アイコン */}
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '4px',
              bgcolor: '#EEF2FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <BusinessIcon sx={{ fontSize: 13, color: '#5C6BC0' }} />
          </Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#4a5568', whiteSpace: 'nowrap', mr: 0.25 }}>
            {event.company_name}
          </Typography>
          {/* カテゴリバッジ */}
          <Box
            sx={{
              display: 'inline-block',
              fontSize: '0.62rem',
              fontWeight: 700,
              borderRadius: '3px',
              px: 0.75,
              py: '1px',
              bgcolor: `${catColor}18`,
              color: catColor,
              border: `1px solid ${catColor}40`,
              lineHeight: 1.5,
              whiteSpace: 'nowrap',
            }}
          >
            {CATEGORY_LABELS[event.category]}
          </Box>
          {event.is_participated && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                fontSize: '0.6rem',
                fontWeight: 700,
                borderRadius: '3px',
                px: 0.75,
                py: '1px',
                bgcolor: '#1565c0',
                color: '#fff',
                lineHeight: 1.5,
                whiteSpace: 'nowrap',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: '0.65rem' }} />
              登録済み
            </Box>
          )}
        </Stack>

        {/* タイトル（2行まで） */}
        <Typography
          sx={{
            fontSize: '0.88rem',
            fontWeight: 700,
            color: '#1a2533',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.4,
          }}
        >
          {event.title}
        </Typography>

        {/* 場所・定員・締切 + 詳細ボタン */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.25, flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', minWidth: 0 }}>
              {isOnline ? (
                <LanguageIcon sx={{ fontSize: 11, color: '#8899aa', flexShrink: 0 }} />
              ) : (
                <LocationOnIcon sx={{ fontSize: 11, color: '#8899aa', flexShrink: 0 }} />
              )}
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: '#6b7c8e',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  maxWidth: 160,
                }}
              >
                {isOnline ? 'オンライン' : event.location}
              </Typography>
            </Box>
            {event.capacity != null && event.current_participants != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <PeopleIcon sx={{ fontSize: 11, color: '#8899aa' }} />
                <Typography sx={{ fontSize: '0.7rem', color: '#6b7c8e', whiteSpace: 'nowrap' }}>
                  {event.current_participants}/{event.capacity}名
                  {event.capacity - event.current_participants <= 5 && event.capacity - event.current_participants > 0 && !isPast && (
                    <Box component="span" sx={{ ml: 0.5, color: '#e53e3e', fontWeight: 700 }}>
                      残り{event.capacity - event.current_participants}名
                    </Box>
                  )}
                  {event.current_participants >= event.capacity && !isPast && (
                    <Box component="span" sx={{ ml: 0.5, color: '#e53e3e', fontWeight: 700 }}>満員</Box>
                  )}
                </Typography>
              </Box>
            )}
            <DeadlineLabel event={event} />
          </Stack>

          {/* 詳細ボタン（右下） */}
          <Box
            sx={{
              flexShrink: 0,
              fontSize: '0.68rem',
              fontWeight: 700,
              color: isPast ? '#aab5be' : '#1976d2',
              border: `1px solid ${isPast ? '#c0ccd8' : '#1976d2'}`,
              borderRadius: '6px',
              px: 1,
              py: 0.4,
              whiteSpace: 'nowrap',
              lineHeight: 1.5,
            }}
          >
            詳細を見る
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── メインコンポーネント ──────────────────────────────────────────

export const WeekViewCalendar: React.FC<WeekViewCalendarProps> = ({
  weekStart,
  weekEnd,
  calendarDots,
  events,
  isLoading,
  selectedDate,
  onDateClick,
  onWeekChange,
  onSwitchToMonth,
  onEventDetail,
}) => {
  const todayStr = getTodayStr();

  const dotMap = useMemo(
    () => new Map(calendarDots.map((d) => [d.date, d.count])),
    [calendarDots],
  );

  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const headerMonth = formatHeaderMonth(weekStart);

  const handlePrev = () => onWeekChange(addDays(weekStart, -7), addDays(weekEnd, -7));
  const handleNext = () => onWeekChange(addDays(weekStart, 7), addDays(weekEnd, 7));
  const handleToday = () => {
    const sun = getSundayOfWeek(todayStr);
    onWeekChange(sun, addDays(sun, 6));
  };

  /** 選択日ラベル文字列 */
  const selectedLabel = useMemo(() => {
    if (!selectedDate) return null;
    const d = new Date(`${selectedDate}T00:00:00`);
    const count = dotMap.get(selectedDate) ?? 0;
    return `${d.getMonth() + 1}月${d.getDate()}日（${DOW_LABELS[d.getDay()]}）のイベント — ${count}件`;
  }, [selectedDate, dotMap]);

  /** 選択日のイベントを時刻順にソート */
  const selectedEvents = useMemo(() => {
    if (!selectedDate) return [];
    return [...events]
      .filter((e) => e.event_date === selectedDate)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [events, selectedDate]);

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* ─── ヘッダー（月ビューと同じホワイト背景・同じボタンスタイル） ─── */}
      <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {/* 左：年月タイトル */}
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
            {headerMonth}
          </Typography>

          {/* 右：TODAY + ナビ矢印 */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              size="small"
              onClick={handleToday}
              sx={{
                minWidth: 0,
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 600,
                px: 1,
                py: 0.25,
                border: '1px solid #E0E0E0',
                borderRadius: 1,
                lineHeight: 1.5,
                '&:hover': { bgcolor: 'action.hover', borderColor: '#BDBDBD' },
              }}
            >
              TODAY
            </Button>
            <IconButton
              size="small"
              onClick={handlePrev}
              sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 0.5 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleNext}
              sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 0.5 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* ─── 7日グリッド ─── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          px: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {weekDays.map((day) => {
          const dateStr = toLocalDateStr(day);
          const dow = day.getDay();
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;
          const count = dotMap.get(dateStr) ?? 0;
          const isSun = dow === 0;
          const isSat = dow === 6;

          // 月ビューと同一の色定義
          const dowColor  = isSun ? '#EF5350' : isSat ? '#1976D2' : '#616161';
          const numColor  = isSun ? '#EF5350' : isSat ? '#1976D2' : '#212121';

          return (
            <Box
              key={dateStr}
              onClick={() => onDateClick(dateStr)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 0.75,
                px: 0.25,
                mx: 0.25,
                mt: 0.5,
                cursor: 'pointer',
                borderRadius: 1,
                position: 'relative',
                transition: 'background 0.15s',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {/* 曜日ラベル（月ビューと同サイズ・同色） */}
              <Typography
                component="span"
                sx={{
                  color: dowColor,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  mb: 0.25,
                }}
              >
                {DOW_LABELS[dow]}
              </Typography>

              {/* 日付番号（月ビューと同じ丸スタイル） */}
              <Box
                sx={{
                  width: 'min(42px, 80%)',
                  aspectRatio: '1 / 1',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: isSelected ? '8px' : '50%',
                  bgcolor: isSelected
                    ? '#1976D2'
                    : isToday
                      ? '#E3F2FD'
                      : 'transparent',
                  transition: 'background-color 0.15s',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 'clamp(0.75rem, 1.5vw, 1rem)',
                    fontWeight: isToday || isSelected ? 700 : 400,
                    color: isSelected ? '#fff' : numColor,
                    lineHeight: 1,
                  }}
                >
                  {day.getDate()}
                </Typography>
              </Box>

              {/* ドット（月ビューと同じ #1976D2） */}
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', mt: 0.25, flexShrink: 0, bgcolor: count > 0 ? (isSelected ? '#fff' : '#1976D2') : 'transparent' }} />
            </Box>
          );
        })}
      </Box>

      {/* ─── 選択日ラベル（月ビューの #E3F2FD / #1976D2 トーンに統一） ─── */}
      {selectedDate && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 3,
            py: 1,
            bgcolor: '#E3F2FD',
            borderBottom: '1px solid #BBDEFB',
          }}
        >
          <Box
            sx={{ width: 3, height: 14, bgcolor: '#1976D2', borderRadius: '2px', flexShrink: 0 }}
          />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1565C0' }}>
            {selectedLabel}
          </Typography>
        </Box>
      )}

      {/* ─── イベントリスト ─── */}
      <Box sx={{ bgcolor: '#fff', minHeight: 80 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !selectedDate ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 5,
              color: '#aab5be',
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
            <Typography sx={{ fontSize: '0.85rem' }}>
              日付を選択するとイベントが表示されます
            </Typography>
          </Box>
        ) : selectedEvents.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 5,
              color: '#aab5be',
            }}
          >
            <Typography sx={{ fontSize: '1.5rem', mb: 1, opacity: 0.4 }}>🎉</Typography>
            <Typography sx={{ fontSize: '0.85rem' }}>この日のイベントはありません</Typography>
          </Box>
        ) : (
          selectedEvents.map((event) => (
            <CompactItem key={event.id} event={event} onDetail={onEventDetail} />
          ))
        )}
      </Box>
    </Paper>
  );
};
